import { SHARED_SECRET, WEB_APP_URL } from '../config';

// ── SALES CRM API (server/routes/crm.js) ─────────────────────
// Salespeople, Lead Managers and Sales Admins send the crmToken they got at
// sign-in; the admin sends the admin password.

export type CrmAuth = { crmToken: string } | { adminPassword: string };

// The call flow: Brochure Sent (form + recording) → Poll Sent → the poll's
// status (Interested / Not Interested / No Response) → Interested pays → Converted.
// Or the call books a meeting with a Student Coordinator (Log call →
// Coordinator Meeting, not a brochure: Meeting Booked); once the student
// joins it, the coordinator sends the brochure and handles the lead.
// Junk Lead: nobody picked up after every salesperson and Lead Manager tried
// 3 times; nobody has it.
// P2P: Path to Placement (server/routes/p2p.js) from the P2P meeting booked
// (Log call → P2P Meeting) to its end; a Lead Manager handles it on the P2P board.
export type Stage = 'New' | 'Not Picked' | 'Call Later' | 'Meeting Booked' | 'P2P' | 'Poll Sent' | 'Interested' | 'No Response' | 'Cold Lead' | 'Not Interested'
  | 'Junk Lead' | 'Future Prospect' | 'Direct Visit' | 'Converted';
export type Outcome = 'Not Picked' | 'Call Later' | 'Cold Lead' | 'Brochure Sent' | 'Coordinator Meeting' | 'P2P Meeting' | 'Converted';
export type PitchResult = 'Poll Sent' | 'Future Prospect' | 'Direct Visit';
export type PollAnswer = 'Interested' | 'Not Interested' | 'No Response';
export type SalesRole = 'Salesperson' | 'Lead Manager' | 'Sales Admin';
// A shift ('A' / 'B') with its times; null = the whole working day
export interface Shift { key: 'A' | 'B'; name: string; start: string; end: string }
export interface MyShift { week: string; thisWeek: Shift | null; nextWeek: Shift | null; onNow: boolean }
// Messaged on WhatsApp directly, or filled a form ('' = not set)
export type LeadType = '' | 'WhatsApp' | 'Form';

export interface CrmLead {
  id: string; leadNo: number; name: string; phone: string; email: string;
  source: string; course: string; notes: string; leadType: LeadType; mode: '' | 'Online' | 'Offline';
  // pitched: a brochure was sent (the form + recording)
  degree: string; passedOutYear: string; profile: string; location: string; pitchNotes: string; pitched: boolean;
  pollSentAt: string | null; interestedAt: string | null;
  stage: Stage; ownerId: string; ownerName: string; ownerDay: string; ownerCount: number;
  waiting: '' | 'after-hours' | 'nobody-free'; waitingSince: string | null;
  nextAt: string | null; lastCallAt: string | null; lastOutcome: string;
  callCount: number; notPicked: number; reason: string; convertedAt: string | null; enquiries: number;
  // A website form put them first in To call, until a call is logged
  webForm?: { at: string; via: string; again: boolean } | null;
  createdAt: string; updatedAt: string;
  movesTomorrow: boolean; movedToYou: boolean; visitDue: boolean; closedAt: string | null;
  pollDue: boolean; paymentDue: boolean;
  // Not taken yet: its owner's tries (of triesEach) and where it goes next,
  // "Tries: 1 of 3 · goes to Shift 2 at 6:00 PM" (older servers: none)
  tries?: number | null; triesEach?: number; turnNote?: string;
  // Not Picked calls that didn't ring (of noRingLimit: then Junk Leads), while
  // they still can send it there; rang: a call in this round rang
  noRing?: number; noRingLimit?: number; rang?: boolean;
  // pollLater: the poll is set to go out later (not sent yet); paymentLinked:
  // their payment record is linked (a coordinator's lead: the student's
  // registration form came in, at formAt)
  pollLater?: boolean; paymentLinked?: boolean; formAt?: string | null;
  // The coordinator meeting booked now or last; coordinatorId: the Student
  // Coordinator handling the lead since the student joined one ('' = the salesperson)
  meeting?: LeadMeeting | null; coordinatorId?: string; coordinatorName?: string;
  // Path to Placement: since when and how they came for it ('Website': the
  // /p2p page form, 'Call'), and their P2P case now (caseId '' = none open;
  // the last one's step and outcome stay); their resume (open it with crm.resumeLink)
  p2p?: { since: string; via: string; caseId?: string; step?: P2PStep | ''; lmName?: string; csName?: string; meetingAt?: string | null; outcome?: string } | null;
  resume?: { fileName: string; size: number; at: string | null } | null;
  // From the /p2p page form: "Python" · "Strong" (older servers: none)
  skills?: Skill[];
}
export interface Skill { name: string; level: string }

// ── Path to Placement (server/routes/p2p.js) ──────────────────
// One student's way through P2P: meeting (a Lead Manager takes it, adds the
// link, marks joined) → fee (the test fee link) → test (time, then the
// result) → pass (the balance link, or ATS) / fail (ATS, or a refund) → ats
// (the ATS resume to make and send) → done (outcome).
// A student who paid the test fee on the payment page (/p2p/pay) with no
// meeting starts at "test" (via 'Payment page', no meeting, nobody booked it)
// and waits for a Lead Manager to take them. The balance can be paid in parts:
// each part paid is a Paid row (restAfter: what was left), the rest a new link,
// and the date the student asked for the rest (rest) waits for the Lead
// Manager's (or the admin's) OK.
export type P2PStep = 'meeting' | 'fee' | 'test' | 'pass' | 'fail' | 'ats' | 'done';
export interface P2PPay {
  id: string; kind: 'test' | 'balance' | 'ats'; label: string; amount: number; status: 'Pending' | 'Paid' | 'Cancelled'; url: string;
  sentAt: string | null; sentByName: string; payBy: string | null; paidAt: string | null; paymentId: string; method: string;
  restAfter?: number; restBy?: string | null;
  refund: { status: string; amount: number; at: string; byName: string; id: string; processedAt: string | null } | null;
}
export interface P2PCan {
  take: boolean; takeOver: boolean; link: boolean; joined: boolean; move: boolean; cancel: boolean; resend: boolean; notPaying: boolean;
  setTest: boolean; result: boolean; balance: boolean; offerAts: boolean; refund: boolean; sendAts: boolean; restDate?: boolean;
  // A Career Strategist: a student nobody has, or another Career Strategist's
  csTake?: boolean; csTakeOver?: boolean;
}
export interface P2PCase {
  id: string; leadId: string; leadNo: number; name: string; phone: string; email: string; degree: string; passedOutYear: string; via: string; note: string;
  skills?: Skill[];
  bookedById: string; bookedByName: string; bookedAt: string | null; resumeDueAt: string | null;
  meetingAt: string | null; meetingWhen: string; minutes: number; lmId: string; lmName: string; takenAt: string | null; link: string; joinedAt: string | null;
  // The Career Strategist who has them once the test fee is paid
  csId?: string; csName?: string; csAt?: string | null;
  // The balance paid so far; the date the student asked for the rest (older servers: none)
  balance?: { paid: number; due: number; total: number; payBy: string | null } | null;
  rest?: { at: string; when: string; status: 'asked' | 'approved'; askedAt: string | null; byName: string; changed: boolean } | null;
  step: P2PStep; stepLabel: string; testAt: string | null; testWhen: string; testWhere: string; tests: number;
  result: '' | 'Pass' | 'Fail'; score: string; resultAt: string | null; atsAt: string | null;
  outcome: string; outcomeAt: string | null; outcomeByName: string; reason: string;
  atsResume: { fileName: string; size: number; at: string } | null; atsSentAt: string | null;
  payments: P2PPay[]; next: { text: string; tone: 'red' | 'amber' | 'purple' | 'green' | 'gray' };
  wa: { meeting: string; pay: string; test: string }; mine: boolean; can: P2PCan;
  stuck?: string;
}
export interface P2PPrices { p2pTestFee: number; p2pBalance: number; p2pAfterFee: number; p2pAtsPrice: number }
export type P2PTab = 'take' | 'mine' | 'paid' | 'open' | 'done' | 'booked';
export interface LeadMeeting { id: string; at: string | null; status: MeetingStatus | ''; coordinatorId: string; coordinatorName: string; link: string }

// ── Student Coordinator meetings (server/routes/crmMeetings.js) ──
export type MeetingStatus = 'Booked' | 'Link Ready' | 'Confirmed' | 'Joined' | 'Not Joined' | 'Cancelled';
export interface Meeting {
  id: string; leadId: string; leadNo: number; name: string; course: string; mode: string; stage: Stage | ''; ownerName: string;
  // open: no coordinator has taken it yet (the first to add a link takes it;
  // coordinatorName is then "a coordinator")
  open?: boolean; coordinatorId: string; coordinatorName: string; bookedByName: string; salesPersonId: string; salesPersonName: string;
  date: string; slot: string; dayLabel: string; timeLabel: string; when: string; startsAt: string; endsAt: string;
  status: MeetingStatus; active: boolean; started: boolean; ended: boolean;
  // app: 'Microsoft Teams' | 'Google Meet' | 'Zoom' | '' (another link); a
  // pasted invite's Meeting ID and passcode
  link: string; app?: string; joinId?: string; passcode?: string;
  linkAt: string | null; confirmedAt: string | null; confirmedByName: string; outcomeAt: string | null; note: string; moves: number;
  cancelledAt: string | null; cancelledByName: string; cancelReason: string;
  // The coordinator gets the student's details once their link is in
  detailsLocked: boolean; withCoordinator: string;
  phone?: string; email?: string; degree?: string; passedOutYear?: string; profile?: string; location?: string;
  pitchNotes?: string; notes?: string; source?: string; leadType?: string;
  // WhatsApp: the number with 91, and the ready message once the link is in
  waPhone?: string; invite?: string;
}
// A time to book: no coordinator (free: how many are free then; the first to
// add a link takes the meeting). coordinatorId is '' (older screens sent one).
export interface MeetingSlot { coordinatorId: string; coordinatorName: string; date: string; slot: string; label: string; startsAt: string; free?: number }
export interface SlotDay { date: string; label: string; slots: MeetingSlot[] }
export interface SlotsData { minutes: number; closeBefore: number; days: SlotDay[]; coordinators: { id: string; name: string; hasTimes: boolean }[] }
export interface MeetingPick { coordinatorId?: string; date: string; slot: string }
export interface SalesMeetingsData {
  now: string; toSend: Meeting[]; waiting: Meeting[]; upcoming: Meeting[]; after: Meeting[]; past: Meeting[];
  coordinators: { id: string; name: string }[];
}
// What came of a range's meetings (by their time), and what the coordinator did in it
// (collected: what the students converted have paid, from their payment records)
export interface MeetTotals {
  booked: number; joined: number; notJoined: number; cancelled: number; upcoming: number; toMark: number; joinPct: number;
  brochures?: number; pollsSent: number; pollsLater?: number; interested: number; notInterested: number; noResponse: number; converted: number; collected: number;
  convertedOfJoined: number; conversionPct: number;
}
export interface MeetDashboardData {
  today: string; range: { key: string; from: string; to: string; label: string };
  coordinator: { id: string; name: string } | null; totals: MeetTotals;
  now: { polls: number; pollsDue: number; interested: number; paymentsDue: number; noResponse: number };
  weeks: { week: string; label: string; booked: number; joined: number; converted: number }[];
  salespeople: { id: string; name: string; booked: number; joined: number; converted: number }[];
  people: (MeetTotals & { id: string; name: string; active: boolean; handling: number })[];
  coordinators: { id: string; name: string }[];
}
// 10% of the before-placement fee, once it is fully paid
export interface IncentiveRow {
  id: string; name: string; course: string; mode: string; enrollDate: string; courseFee: number; totalPaid: number; balance: number;
  status: 'Fully Paid' | 'Partly Paid' | 'Ran Away'; incentive: number; waiting: number; paidOut: boolean; paidOutAt: string | null;
  coordinatorId: string; coordinatorName: string;
}
export interface IncentiveTotals { students: number; fullyPaid: number; ranAway: number; earned: number; paidOut: number; toBePaid: number; waiting: number }
export interface MeetIncentiveData {
  pct: number; totals: IncentiveTotals; rows: IncentiveRow[]; months: { month: string; students: number; earned: number; waiting: number }[];
  coordinators?: (IncentiveTotals & { id: string; name: string })[];
}

export interface CrmFile { path: string; contentType: string; size: number; fileName: string }
export interface CrmFileInfo { fileName: string; size: number; contentType: string }

export interface CrmActivity {
  // meeting: a coordinator meeting (result: Booked, Link added, Sent to student,
  // Joined, Didn't join, Cancelled…) and what the coordinator did after it;
  // cancelled-call: a call undone by an approved fix request (cancelled);
  // deleted / restored: the lead went to Deleted leads, or came back;
  // junk: off to Junk Leads (nobody picked up after everyone's tries, the
  // phone didn't ring twice, or a manager, byName, put it there);
  // heard: a Not Picked call's result (Switched off, Ringing, no answer…)
  id: string; type: 'added' | 'assigned' | 'moved' | 'waiting' | 'junk' | 'call' | 'poll' | 'enquired' | 'edited' | 'meeting' | 'p2p' | 'cancelled-call' | 'deleted' | 'restored'; at: string;
  leadId: string; leadNo: number; leadName: string;
  byId: string; byName: string; toId: string; toName: string; fromId: string; fromName: string;
  reason: string; outcome: string; result: string; visit: string; note: string; nextAt: string | null; pollSentAt: string | null;
  amount: number; source: string; recording: CrmFileInfo | null; screenshot: CrmFileInfo | null;
  cancelled?: { byName: string; at: string | null; reason: string; should: string } | null;
  heard?: string;
}

// ── Call fix requests: a wrongly logged call, undone once a manager approves ──
export type FixKind = 'wrong-result' | 'wrong-lead';
export interface CallFix {
  id: string; leadId: string; leadNo: number; leadName: string; activityId: string; callAt: string | null; outcome: string; result: string;
  byId: string; byName: string; kind: FixKind; should: string; reason: string;
  status: 'Pending' | 'Approved' | 'Rejected'; decidedByName: string; decidedAt: string | null; note: string; createdAt: string;
  // For the approver: the lead now, and why it can't be undone any more ('' = it can)
  lead?: { stage: Stage; ownerName: string } | null; blocked?: string;
}
// On the lead page: the last call if it can be undone, may I ask, the lead's requests
// A website form filled by a number we already know (the "Forms again" list)
export interface FormAgainItem {
  id: string; kind: 'repeat' | 'student'; form: string; at: string; leadId: string; leadNo: number;
  name: string; phone: string; email: string; degree: string; passedOutYear: string;
  skills: { name: string; level: string }[]; note: string; oldName: string; oldEmail: string;
  beforeStage: string; beforeOwner: string; enquiries: number; held: boolean;
  stage: string; ownerName: string; waiting: string; resume: { fileName: string; size: number } | null;
}

export interface LeadCallFix { activityId: string; canAsk: boolean; requests: CallFix[] }
export interface DeletedLead {
  id: string; leadNo: number; phone: string; name: string; stage: string; ownerName: string;
  reason: string; byName: string; deletedAt: string; daysLeft: number;
}

export interface CrmLists { coldReasons: string[]; notInterestedReasons: string[]; sources: string[]; courses: string[] }
export interface CrmPerson { id: string; name: string; role: SalesRole; takesLeads: boolean; paused: boolean }

export interface CrmMe {
  // takesCalls: has leads of their own to call (a Lead Manager always: the
  // ones the salespeople couldn't reach; takesLeads: new leads too)
  me: { id: string; name: string; role: SalesRole | 'Admin'; isManager: boolean; takesLeads: boolean; takesCalls?: boolean };
  lists: CrmLists; profiles: string[]; stages: Stage[]; maxOwners: number; people?: CrmPerson[]; leadTypes?: LeadType[]; modes?: string[];
  shifts?: Shift[];
  work?: { start: string; end: string; days: number[]; resultHours: number; retryHours?: number; pollHours?: number; pollMorning?: string };
  // The holidays ahead: no follow-up can be set on one
  holidays?: Holiday[];
  meeting?: { outcome?: string; result: string; minutes: number };
  // A P2P meeting (Log call → P2P Meeting): its length, and the minutes the
  // salesperson has to add a missing resume (older servers: none)
  p2p?: { outcome: string; minutes: number; resumeMinutes: number };
  // A call fix request: what the call should have been
  fixShould?: string[];
  // Not Picked: what the caller heard; noRingLimit of noRing on a lead nobody
  // talked to and that never rang send it to Junk Leads (older servers: none)
  notPicked?: { rang: string[]; noRing: string[]; noRingLimit: number };
  // A manager putting leads in Junk Leads: the reasons offered
  junkReasons?: string[];
}

// ── Holidays ──
// The office's own (set in the CRM) or the institute's (the admin's Mentor Calendar)
export interface Holiday { date: string; name: string; kind: 'office' | 'institute'; byName: string; label: string; past?: boolean; isToday?: boolean }
// Today's or a coming one: until its last day (in a row), back the working day after
export interface HolidayRun extends Holiday { until: string; untilLabel: string; back: string; backLabel: string }
export interface HolidayNote { today: HolidayRun | null; next: HolidayRun | null }
export interface HolidaysData { today: string; canEdit: boolean; holidays: Holiday[]; note: HolidayNote }

// ── The Monitor ──
// The numbers. In the EOD report (and Daily EOD): what someone did that day
// (calls and call outcomes count calls; the rest a lead once a day; pitched =
// brochures sent; visits = Direct Visits booked; meetings = coordinator
// meetings booked, not brochures; a poll counts from when it goes out). On
// Team numbers the steps (callLater … converted) hold each lead once: where it
// is now, or for a period that is over, where it stood at the end of it.
// visitsCame / visitsMissed: a visit's next call said whether they came.
export interface Counts {
  calls: number; talked: number; notPicked: number; callLater: number; cold: number;
  pitched: number; visits: number; future?: number; meetings: number; pollsSent: number; interested: number; notInterested: number; noResponse: number; converted: number;
  visitsCame: number; visitsMissed: number;
}
// pollsLater: polls set to go out later today (not sent yet); givenNew /
// givenMoved: the leads given, handed to them directly or from someone else
export type CountKey = keyof Counts | 'given' | 'givenNew' | 'givenMoved' | 'pollsLater';
// heard: Not Picked calls by what they heard ('': not recorded)
export interface MonitorRow extends Counts {
  id: string; name: string; role: string; active: boolean; inTurn: boolean; given: number; givenNew?: number; givenMoved?: number; heard?: Record<string, number>; pollsLater?: number; pollsDue?: number; conversionPct: number; daysWorked: number;
  firstCallAt: string | null; lastCallAt: string | null; hours?: number[];
  now?: { shift: Shift | null; onShift: boolean; onLeave: boolean; halfDay: { from: string; to: string } | null; awayNow: boolean; paused: boolean };
}
// (the step counts hold each lead once; pollsDue: in Poll sent, its status due now)
export type MonitorTotals = Counts & { given: number; givenNew?: number; givenMoved?: number; heard?: Record<string, number>; pollsLater?: number; pollsDue?: number; conversionPct: number };
export interface MonitorDay extends Counts { day: string; label: string; working: boolean; holiday: string; given: number }
export interface MonitorData {
  today: string; now: string; working: boolean;
  range: { key: string; from: string; to: string; label: string }; compare: { from: string; to: string; label: string };
  // over: the period has ended, so the numbers stand as at its end; heardList: what can be heard on a Not Picked call
  over?: boolean; heardList?: { rang: string[]; noRing: string[] };
  person: { id: string; name: string } | null;
  team: MonitorTotals; before: MonitorTotals; people: MonitorRow[]; days: MonitorDay[];
  hours: { from: number; to: number } | null; holiday: HolidayNote;
  // The whole team only: each Student Coordinator's meetings in the range
  coordinators?: { rows: CoordMonitorRow[]; team: CoordNumbers; open: number } | null;
}
// Meetings in the range (by their time) with them; converted: marked
// Converted in it; conversion: of the students who joined, how many have paid
export interface CoordNumbers { booked: number; joined: number; notJoined: number; converted: number; conversionPct: number; convertedOfJoined: number; upcoming: number; toMark: number }
export interface CoordMonitorRow extends CoordNumbers { id: string; name: string; active: boolean }
export type CoordKind = 'booked' | 'joined' | 'notJoined' | 'converted';
export interface CoordLeadRow { id: string; leadId: string; leadNo: number; leadName: string; at: string; status: string; coordinatorName: string; salesPersonName: string; lead: CrmLead | null }
export interface MonitorRange { range?: string; from?: string; to?: string }
// nowStage: a period that is over, and the lead has left that box since
export interface MonitorLeadRow extends CrmActivity { lead: CrmLead | null; nowStage?: string }
export interface LeadStatusCounts { stages: Record<string, number>; open: number; toCall: number; pollsDue: number; pollsLate: number; paymentsDue: number; late: number }
export interface LeadStatusRow extends LeadStatusCounts { id: string; name: string; role: string; active: boolean; inTurn: boolean; onLeave: boolean; paused: boolean }
export interface LeadStatusData {
  today: string; now: string; lateHours: number; stages: Stage[]; rows: LeadStatusRow[]; team: LeadStatusCounts;
  waiting: { total: number; afterHours: number; nobodyFree: number; passedAll: number }; holiday: HolidayNote;
}
// sent / made at the end of the day (auto) / not yet (due) / none (missing) / on leave / not a working day (off)
export type EodStatus = 'sent' | 'auto' | 'due' | 'missing' | 'leave' | 'off';
export interface EodDayRow {
  id: string; name: string; role: string; status: EodStatus; halfDay: { from: string; to: string } | null; counts: Counts;
  report: {
    mood: string; highlights: string; challenges: string; plan: string; submittedAt: string | null; auto: boolean;
    typed: { calls: number; demos: number; conversions: number } | null;
  } | null;
}
export interface EodDayData {
  date: string; label: string; today: string; working: boolean; holiday: Holiday | null; eodAt: string;
  rows: EodDayRow[]; team: Counts; prev: string; next: string | null;
}

export interface CrmRules {
  workStart: string; workEnd: string; workDays: number[]; daysPerOwner: number; maxOwners: number;
  retryHours: number; notCalledAt: string; remindBefore: number; lateHours: number; dailyCap: number; order: string[];
  resultHours: number; pollHours: number; shifts: Shift[]; shiftRotate: boolean;
}

// ── Shifts ──
export interface ShiftPerson { id: string; name: string; role: SalesRole; takesLeads: boolean; thisWeek: Shift | null; nextWeek: Shift | null; onNow: boolean }
export interface ShiftRequest {
  id: string; byId: string; byName: string; week: string; fromShift: string; toShift: 'A' | 'B'; fromName: string; toName: string;
  reason: string; status: 'Pending' | 'Approved' | 'Rejected'; decidedByName: string; decidedAt: string | null; note: string; createdAt: string;
}
export interface ShiftsData {
  shifts: Shift[]; rotate: boolean; thisWeek: string; nextWeek: string; workStart: string; workEnd: string;
  me: ShiftPerson | null; team?: ShiftPerson[]; requests: ShiftRequest[];
}

// ── Home pages ──
export interface Level { key: 'starter' | 'bronze' | 'silver' | 'gold' | 'diamond'; name: string; emoji: string; min: number }
// pitched: leads sent a brochure
export interface WeekStats { week?: string; calls: number; talked: number; pitched: number; pollsSent?: number; interested?: number; converted: number; base: number; pct: number }
export interface MyWeekData {
  today: string; week: { from: string; to: string; lastFrom: string }; name: string;
  levels: Level[]; level: Level; next: Level | null; needMore: number | null; reaches: Level | null;
  thisWeek: WeekStats; lastWeek: WeekStats; weeks: (WeekStats & { week: string })[];
  todayWork: { calls: number; pitched: number; pollsSent?: number; converted: number; callNow: number; fresh: number; pollsDue: number; payments: number; laterToday: number };
  closest: CrmLead[];
  team: { id: string; name: string; converted: number; pitched: number; pct: number; calls: number; me: boolean; rank: number }[];
  goal: { target: number; headline: string; message: string; emoji: string; reward: string; admissions: number } | null;
  quote: { quote: string; author: string } | null;
  notInterested: { total: number; leads: (CrmLead & { days: number | null })[] };
  shift: MyShift | null;
}
export interface LeadDeskData {
  today: string; working: boolean; week: { from: string };
  flow: {
    addedToday: number; handedOutToday: number; movedToday: number; notCalled: number; movingTomorrow: number;
    waiting: { total: number; afterHours: number; nobodyFree: number; passedAll: number }; lateFollowUps: number; addedThisWeek: number;
    leadRequests?: number; shiftRequests?: number; callFixes?: number; pollsDue?: number;
  };
  // Not picked after everyone's tries: the newest, how many today and in all
  junk?: { total: number; today: number; leads: CrmLead[] };
  // Who calls leads not taken, in order: the salespeople, then the Lead
  // Managers (newLeads: in the turn for new leads too)
  order: { id: string; name: string; role?: SalesRole; newLeads?: boolean; paused: boolean; onLeave: boolean; halfDay: { from: string; to: string } | null; awayNow: boolean; shift: Shift | null; onShiftNow: boolean }[];
  team: TeamRow[]; addsByDay: { day: string; added: number }[]; sources: { source: string; n: number }[];
  types: { WhatsApp: number; Form: number; none: number };
  holiday?: HolidayNote;
}
export interface BoardRow extends WeekStats { id: string; name: string; lastConverted: number; lastPct: number; level: Level; rank: number }
export interface SalesOverviewData {
  today: string; week: { from: string; to: string };
  thisWeek: WeekStats; lastWeek: WeekStats & { week: string }; weeks: (WeekStats & { week: string })[];
  board: BoardRow[]; levels: Level[]; teamLevel: Level;
  pipeline: { count: number; leads: DashLead[] }; visits: number; resultsPending: { total: number; overdue: number };
  lost: { reason: string; n: number }[];
  holiday?: HolidayNote;
}

export interface TeamRow {
  id: string; name: string; role: SalesRole; inTurn: boolean; paused: boolean; onLeave: boolean;
  givenToday: number; movedToday: number; calledToday: number; talkedToday: number;
  toCall: number; notTaken: number; open: number; lateFollowUps: number; pollsDue: number; interested: number;
  halfDay: { from: string; to: string } | null; awayNow: boolean; takesLeads: boolean;
  shift: Shift | null; nextShift: Shift | null; onShiftNow: boolean;
  // Sales attendance on: when they pressed Start work / End work today
  // (null when attendance is off; older servers: missing)
  work?: { startAt: string | null; endAt: string | null; lateMinutes: number } | null;
}

// ── Sales attendance (server/routes/crmAttendance.js) ──
// Start work / End work at the office; offices, how near counts (metres),
// the grace minutes and the lunch break come from the admin
export interface Office { id: string; name: string; lat: number; lng: number }
// on: an office is set; active: on and its first day has come (an office
// added once the working day began counts from the next one, sinceLabel)
export interface AttSettings { on: boolean; active?: boolean; offices: Office[]; range: number; grace: number; lunch: { start: string; end: string }; since: string; sinceLabel?: string }
// on-time / late: started · not-yet: due later today · not-started: due, not
// pressed · absent: a day over with no start · leave / holiday / off: not
// due · future · none: before attendance began or before they joined
export type AttStatus = 'on-time' | 'late' | 'not-yet' | 'not-started' | 'absent' | 'leave' | 'holiday' | 'off' | 'future' | 'none';
export interface AttPlace { office: string; via: '' | 'app' | 'website' | 'manual'; distance: number | null }
export interface AttDay {
  date: string; label: string; status: AttStatus; off: '' | 'leave' | 'holiday' | 'off';
  from: string; to: string; shiftName: string; halfDay: { from: string; to: string } | null;
  startAt: string | null; endAt: string | null; endAuto: boolean;
  lateMinutes: number; earlyMinutes: number; workedMinutes: number;
  start: AttPlace | null; end: AttPlace | null; setByName: string; note: string;
}
export interface AttRequest {
  id: string; byId: string; byName: string; date: string; dateLabel: string; kind: 'start' | 'end'; time: string; reason: string;
  status: 'Pending' | 'Approved' | 'Rejected'; decidedByName: string; decidedAt: string | null; note: string; createdAt: string;
}
export interface AttTotals {
  working: number; present: number; onTime: number; late: number; lateMinutes: number; early: number; earlyMinutes: number;
  noEnd: number; absent: number; leave: number; workedMinutes: number; absentDates: { date: string; label: string; days: number }[];
}
export type AttMe = AttSettings & { today: string; now: string; needsStart: boolean; pressesStart?: boolean; day: AttDay | null; pending: AttRequest[] };
export type AttTeamDay = AttSettings & { date: string; label: string; today: string; working: boolean; people: (AttDay & { id: string; name: string })[]; counts: Record<string, number>; pending: number };
export type AttPersonMonth = AttSettings & { month: string; today: string; person: { id: string; name: string }; days: AttDay[]; totals: AttTotals };
export type AttTeamMonth = AttSettings & { month: string; today: string; dates: string[]; people: { id: string; name: string; totals: AttTotals; marks: AttStatus[] }[] };
// A reading of where the phone (or browser) is
export interface AttReading { lat: number; lng: number; accuracy?: number; mocked?: boolean; via: 'website' | 'app' }

export interface CheckRow { i: number; name: string; phone: string; source: string; course: string; leadType: LeadType; mode: string; check: 'new' | 'existing' | 'student' | 'invalid' | 'duplicate'; owner: string; stage: string }
export interface LeadRow { name?: string; phone?: string; email?: string; course?: string; source?: string; notes?: string; leadType?: string; mode?: string }

// pitched: brochures sent
export interface DashKpis { calls: number; talked: number; pitched: number; pollsSent: number; interested: number; converted: number; given: number; added: number; movedAway: number; conversionPct: number }
export interface DashPerson { id: string; name: string; role: string; active: boolean; given: number; movedAway: number; calls: number; talked: number; pitched: number; pollsSent: number; interested: number; converted: number; conversionPct: number; firstCallMins: number | null }
export interface DashLead { id: string; leadNo: number; name: string; phone: string; ownerName: string; nextAt: string | null; course: string }
export interface CrmDashboardData {
  range: { key: string; from: string; to: string }; byWeek: boolean; today: string;
  scope: { salesPersonId: string; name: string };
  kpis: DashKpis; funnel: { stage: string; n: number }[]; people: DashPerson[];
  sources: { source: string; added: number; talked: number; converted: number }[];
  courses: { course: string; added: number; converted: number }[];
  types: { leadType: string; added: number; talked: number; converted: number }[];
  reasons: { cold: { reason: string; n: number }[]; notInterested: { reason: string; n: number }[] };
  open: Record<string, number>; waiting: number; pipeline: DashLead[]; visits: DashLead[];
  trend: { day: string; leads: number; converted: number }[];
}
// One kind of lead on its own list (Poll Sent, Interested, No Response, Cold Lead, Not Interested)
export type ListStage = 'Poll Sent' | 'Interested' | 'No Response' | 'Cold Lead' | 'Not Interested' | 'Junk Lead';
export interface StageLeadsData {
  stage: ListStage; leads: CrmLead[]; total: number; page: number; pages: number; maxOwners: number;
  owners: { id: string; name: string; n: number }[]; reasons: { reason: string; n: number }[];
}

// A salesperson's own lead waiting for approval
export interface LeadRequest {
  id: string; phone: string; name: string; email: string; course: string; source: string; notes: string;
  leadType: LeadType; mode: string; byId: string; byName: string;
  status: 'Pending' | 'Approved' | 'Rejected'; decidedByName: string; decidedAt: string | null; reason: string;
  leadId: string; leadNo: number; outcome: '' | 'new' | 'moved' | 'reopened' | 'already-theirs'; createdAt: string;
  existing: { leadNo: number; ownerId: string; ownerName: string; stage: Stage } | null;
}

export interface CrmLoginResult {
  ok: true; kind: 'admin' | 'sales'; name: string; role: SalesRole | 'Admin'; isManager: boolean; takesLeads: boolean;
  salesPersonId?: string; crmToken?: string;
}

async function post<T>(action: string, payload: Record<string, unknown>): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(WEB_APP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ action, secret: SHARED_SECRET, ...payload }),
    });
  } catch {
    throw new Error('Network error — check your connection');
  }
  const data = await resp.json().catch(() => null);
  if (!data) throw new Error('Server returned non-JSON response');
  if (!data.ok) {
    // The sign-in no longer works (passcode changed, account made inactive): the CRM site signs out
    if (/sign-in has expired|Sign in again to use the CRM|Admin authentication failed/.test(data.error || '')) {
      window.dispatchEvent(new Event('crm-auth-expired'));
    }
    throw new Error(data.error || 'Server returned an error');
  }
  return data as T;
}

export const crm = {
  login: (password: string) => post<CrmLoginResult>('crmLogin', { password }),
  dashboard: (a: CrmAuth, f: { range?: string; from?: string; to?: string; salesPersonId?: string }) =>
    post<CrmDashboardData & { ok: true }>('crmDashboard', { ...a, ...f }),
  recordings: (a: CrmAuth, f: { salesPersonId?: string; page?: number }) =>
    post<{ ok: true; recordings: CrmActivity[]; total: number; page: number; pages: number }>('crmRecordings', { ...a, ...f }),
  // answers: the polls to update
  dayStart: (a: CrmAuth) => post<{ ok: true; today: string; name: string; fresh: number; answers: CrmLead[]; late: CrmLead[]; todayFollowUps: CrmLead[]; shift: MyShift | null; holiday?: HolidayNote }>('crmDayStart', a),
  allFollowUps: (a: CrmAuth, f: { salesPersonId?: string; stage?: string; leadType?: string }) =>
    post<{ ok: true; today: string; leads: CrmLead[]; counts: Record<string, number>; late: number; maxOwners: number }>('crmAllFollowUps', { ...a, ...f }),
  myWeek: (a: CrmAuth) => post<MyWeekData & { ok: true }>('crmMyWeek', a),
  stageLeads: (a: CrmAuth, f: { stage: ListStage; salesPersonId?: string; reason?: string; q?: string; page?: number }) =>
    post<StageLeadsData & { ok: true }>('crmStageLeads', { ...a, ...f }),
  requestLead: (a: CrmAuth, row: LeadRow) => post<{ ok: true; request: LeadRequest }>('crmRequestLead', { ...a, ...row }),
  myLeadRequests: (a: CrmAuth) => post<{ ok: true; requests: LeadRequest[] }>('crmMyLeadRequests', a),
  leadRequests: (a: CrmAuth, status: string) =>
    post<{ ok: true; status: string; requests: LeadRequest[]; counts: { Pending: number; Approved: number; Rejected: number } }>('crmLeadRequests', { ...a, status }),
  decideLeadRequest: (a: CrmAuth, requestId: string, approve: boolean, reason = '') =>
    post<{ ok: true; request: LeadRequest }>('crmDecideLeadRequest', { ...a, requestId, approve, reason }),
  leadDesk: (a: CrmAuth) => post<LeadDeskData & { ok: true }>('crmLeadDesk', a),
  salesOverview: (a: CrmAuth) => post<SalesOverviewData & { ok: true }>('crmSalesOverview', a),
  me: (a: CrmAuth) => post<CrmMe & { ok: true }>('crmMe', a),
  // polls: Poll Sent leads (their status to update); payments: Interested,
  // waiting to pay. callNow / later still hold due ones of those for older apps.
  // counts.interested: Interested leads waiting to pay; interestedToday /
  // notInterestedToday: the poll answers given today (as the EOD counts them)
  // meetings: their leads' coordinator meetings coming up (links to send first)
  today: (a: CrmAuth) => post<{ ok: true; today: string; tomorrow: string; working: boolean; maxOwners: number;
    counts: { calls: number; callLater?: number; cold?: number; pitched: number; visits?: number; pollsSent: number; converted: number; toCall: number; dueNow: number; pollsDue: number; polls: number; interested: number; interestedToday?: number; notInterestedToday?: number; meetings?: number; linksToSend?: number };
    callNow: CrmLead[]; fresh: CrmLead[]; later: CrmLead[]; polls: CrmLead[]; payments: CrmLead[]; meetings?: Meeting[]; shift: MyShift | null; holiday?: HolidayNote }>('crmToday', a),
  myLeads: (a: CrmAuth, stage = 'open', q = '') => post<{ ok: true; leads: CrmLead[]; counts: Record<string, number>; maxOwners: number }>('crmMyLeads', { ...a, stage, q }),
  followUps: (a: CrmAuth) => post<{ ok: true; today: string; leads: CrmLead[] }>('crmFollowUps', a),
  // lockedBy: why no call can be logged now (a meeting booked, or a coordinator handles it)
  // callFix: the last call if it can be undone (canAsk: by me), and the lead's fix requests
  getLead: (a: CrmAuth, leadId: string) => post<{ ok: true; lead: CrmLead; history: CrmActivity[]; canLog: boolean; canPoll: boolean; maxOwners: number; lockedBy?: string; meeting?: Meeting | null; callFix?: LeadCallFix; p2pCase?: P2PCase | null }>('crmGetLead', { ...a, leadId }),
  // ── Call fix requests ──
  askCallFix: (a: CrmAuth, f: { leadId: string; activityId: string; kind: FixKind; should?: string; reason: string }) =>
    post<{ ok: true; request: CallFix }>('crmAskCallFix', { ...a, ...f }),
  myCallFixes: (a: CrmAuth) => post<{ ok: true; requests: CallFix[] }>('crmMyCallFixes', a),
  callFixes: (a: CrmAuth, status: string) =>
    post<{ ok: true; status: string; requests: CallFix[]; counts: { Pending: number; Approved: number; Rejected: number } }>('crmCallFixes', { ...a, status }),
  decideCallFix: (a: CrmAuth, requestId: string, approve: boolean, note = '') =>
    post<{ ok: true; request: CallFix; lead?: CrmLead }>('crmDecideCallFix', { ...a, requestId, approve, note }),
  // ── Deleting a lead (30 days in Deleted leads, where it can be restored) ──
  deleteLead: (a: CrmAuth, leadId: string, reason: string) => post<{ ok: true; deleted: { leadNo: number; name: string } }>('crmDeleteLead', { ...a, leadId, reason }),
  // ── Website forms filled by a number we already know ──
  formsAgain: (a: CrmAuth, f: { q?: string; form?: string } = {}) => post<{ ok: true; today: number; items: FormAgainItem[] }>('crmFormsAgain', { ...a, ...f }),
  deletedLeads: (a: CrmAuth, q = '') => post<{ ok: true; days: number; leads: DeletedLead[] }>('crmDeletedLeads', { ...a, q }),
  restoreLead: (a: CrmAuth, id: string) => post<{ ok: true; lead: CrmLead }>('crmRestoreLead', { ...a, id }),
  fileLink: (a: CrmAuth, activityId: string, file: 'recording' | 'screenshot') =>
    post<{ ok: true; url: string; contentType: string; fileName: string }>('crmFileLink', { ...a, activityId, file }),
  resumeLink: (a: CrmAuth, leadId: string) =>
    post<{ ok: true; url: string; contentType: string; fileName: string }>('crmFileLink', { ...a, leadId, file: 'resume' }),
  logCall: (a: CrmAuth, payload: Record<string, unknown>) => post<{ ok: true; lead: CrmLead; activity: CrmActivity; meeting?: Meeting }>('crmLogCall', { ...a, ...payload }),
  // The poll's status (nextAt: when an Interested lead said they'd pay; reason: why Not Interested)
  pollStatus: (a: CrmAuth, f: { leadId: string; status: PollAnswer; reason?: string; nextAt?: string; note?: string }) =>
    post<{ ok: true; lead: CrmLead; activity: CrmActivity }>('crmPollStatus', { ...a, ...f }),
  // The menu's counts: due now for whoever takes calls; requests and waiting
  // leads for managers; meeting links a salesperson has to send
  badges: (a: CrmAuth) => post<{ ok: true; today: number; requests: number; waiting: number; meetings?: number; p2p?: number }>('crmBadges', a),
  // ── Path to Placement: the P2P board (server/routes/p2p.js) ──
  p2pList: (a: CrmAuth, tab: P2PTab) => post<{ ok: true; tab: P2PTab; counts: { take: number; mine: number; paid?: number; open: number } | null; cases: P2PCase[]; prices: P2PPrices; payments: boolean }>('p2pList', { ...a, tab }),
  p2pGet: (a: CrmAuth, caseId: string) => post<P2PResult>('p2pGet', { ...a, caseId }),
  p2pAct: (a: CrmAuth | { csToken: string }, action: P2PAction, caseId: string, more: Record<string, unknown> = {}) => post<P2PResult>(action, { ...a, caseId, ...more }),
  p2pPayments: (a: CrmAuth) => post<{ ok: true; payments: boolean; totals: { paid: number; refunded: number; net: number; byKind: Record<string, number> }; list: (P2PPay & { caseId: string; leadId: string; leadNo: number; name: string; phone: string })[] }>('p2pPayments', a),
  // terms: the admin's terms and conditions, one per line (older servers: none)
  p2pSettings: (a: CrmAuth) => post<{ ok: true; prices: P2PPrices; terms?: string[]; payments: boolean }>('p2pSettings', a),
  p2pSaveSettings: (a: CrmAuth, prices: Partial<P2PPrices>) => post<{ ok: true; prices: P2PPrices }>('p2pSaveSettings', { ...a, ...prices }),
  // On every P2P receipt (the PDF emailed when a student pays) and the payment page; admin only
  p2pSaveTerms: (a: CrmAuth, terms: string[]) => post<{ ok: true; terms: string[] }>('p2pSaveTerms', { ...a, terms }),
  // A receipt PDF (base64): a paid payment's, or a sample with these terms (the admin's preview)
  p2pReceipt: (a: CrmAuth, f: { paymentId: string } | { sample: true; terms: string[] }) =>
    post<{ ok: true; pdfBase64: string; fileName: string }>('p2pReceipt', { ...a, ...f }),
  // ── Student Coordinator meetings: the sales side ──
  // The open slots of the next working days (meetingId: one being moved, its own time counts as free)
  meetSlots: (a: CrmAuth, meetingId = '') => post<SlotsData & { ok: true }>('meetSlots', { ...a, meetingId }),
  meetings: (a: CrmAuth, f: { salesPersonId?: string; coordinatorId?: string } = {}) => post<SalesMeetingsData & { ok: true }>('crmMeetings', { ...a, ...f }),
  // Sent to the student (the page opened WhatsApp with the ready message)
  meetConfirm: (a: CrmAuth, meetingId: string) => post<{ ok: true; meeting: Meeting }>('meetConfirm', { ...a, meetingId }),
  meetMove: (a: CrmAuth, meetingId: string, meeting: MeetingPick, note = '') => post<{ ok: true; meeting: Meeting }>('meetMove', { ...a, meetingId, meeting, note }),
  meetCancel: (a: CrmAuth, meetingId: string, reason: string) => post<{ ok: true; meeting: Meeting }>('meetCancel', { ...a, meetingId, reason }),
  // Managers: every coordinator's numbers (coordinatorId: one of them)
  meetDashboard: (a: CrmAuth, f: MonitorRange & { coordinatorId?: string }) => post<MeetDashboardData & { ok: true }>('meetDashboard', { ...a, ...f }),
  // The students behind a coordinator number on the Monitor (coordinatorId '' = every coordinator)
  meetMonitorLeads: (a: CrmAuth, f: MonitorRange & { kind: CoordKind; coordinatorId?: string }) =>
    post<{ ok: true; kind: CoordKind; range: { from: string; to: string; label: string }; total: number; rows: CoordLeadRow[]; maxOwners: number }>('meetMonitorLeads', { ...a, ...f }),
  shifts: (a: CrmAuth) => post<ShiftsData & { ok: true }>('crmShifts', a),
  // from: 'this' or 'next' week; shift '' = the whole day
  setShift: (a: CrmAuth, salesPersonId: string, shift: '' | 'A' | 'B', from: 'this' | 'next') =>
    post<ShiftsData & { ok: true }>('crmSetShift', { ...a, salesPersonId, shift, from }),
  requestShift: (a: CrmAuth, week: 'this' | 'next', reason: string) => post<{ ok: true; request: ShiftRequest }>('crmRequestShift', { ...a, week, reason }),
  shiftRequests: (a: CrmAuth, status: string) =>
    post<{ ok: true; status: string; requests: ShiftRequest[]; counts: { Pending: number; Approved: number; Rejected: number } }>('crmShiftRequests', { ...a, status }),
  decideShiftRequest: (a: CrmAuth, requestId: string, approve: boolean, reason = '') =>
    post<{ ok: true; request: ShiftRequest }>('crmDecideShiftRequest', { ...a, requestId, approve, reason }),
  // ── Sales attendance ──
  attMe: (a: CrmAuth) => post<AttMe & { ok: true }>('attMe', a),
  attStart: (a: CrmAuth, r: AttReading) => post<{ ok: true; day: AttDay }>('attStart', { ...a, ...r }),
  attEnd: (a: CrmAuth, r: AttReading) => post<{ ok: true; day: AttDay }>('attEnd', { ...a, ...r }),
  attAsk: (a: CrmAuth, f: { date: string; kind: 'start' | 'end'; time: string; reason: string }) => post<{ ok: true; request: AttRequest }>('attAsk', { ...a, ...f }),
  // Managers: by status, with counts; a salesperson: their own
  attRequests: (a: CrmAuth, status = 'Pending') =>
    post<{ ok: true; status?: string; requests: AttRequest[]; counts?: { Pending: number; Approved: number; Rejected: number } }>('attRequests', { ...a, status }),
  attDecide: (a: CrmAuth, requestId: string, approve: boolean, reason = '') => post<{ ok: true; request: AttRequest }>('attDecide', { ...a, requestId, approve, reason }),
  // A manager sets someone's times on a day (end '' = none), or clears the day
  attFix: (a: CrmAuth, f: { salesPersonId: string; date: string; start?: string; end?: string; clear?: boolean; note?: string }) =>
    post<{ ok: true; day: AttDay & { id: string; name: string } }>('attFix', { ...a, ...f }),
  attDay: (a: CrmAuth, date = '') => post<AttTeamDay & { ok: true }>('attDay', { ...a, date }),
  attMonth: (a: CrmAuth, month: string) => post<AttPersonMonth & { ok: true }>('attMonth', { ...a, month }),
  attPersonMonth: (a: CrmAuth, month: string, salesPersonId: string) => post<AttPersonMonth & { ok: true }>('attMonth', { ...a, month, salesPersonId }),
  attTeamMonth: (a: CrmAuth, month: string) => post<AttTeamMonth & { ok: true }>('attMonth', { ...a, month }),
  attSettings: (a: CrmAuth) => post<AttSettings & { ok: true }>('attSettings', a),
  attSaveSettings: (a: CrmAuth, s: { offices?: Partial<Office>[]; range?: number; grace?: number; lunch?: { start: string; end: string } }) =>
    post<AttSettings & { ok: true }>('attSaveSettings', { ...a, ...s }),
  addLead: (a: CrmAuth, row: LeadRow & { assignTo?: string }) => post<{ ok: true; status: 'new' | 'existing'; lead: CrmLead | null }>('crmAddLead', { ...a, ...row }),
  checkLeads: (a: CrmAuth, rows: LeadRow[]) => post<{ ok: true; rows: CheckRow[]; counts: Record<string, number> }>('crmCheckLeads', { ...a, rows }),
  importLeads: (a: CrmAuth, rows: LeadRow[], assignTo: string, source: string, leadType: string) =>
    post<{ ok: true; counts: Record<string, number> }>('crmImportLeads', { ...a, rows, assignTo, source, leadType }),
  allLeads: (a: CrmAuth, f: { stage?: string; ownerId?: string; source?: string; leadType?: string; q?: string; waiting?: boolean; page?: number; limit?: number; from?: string; to?: string }) =>
    post<{ ok: true; leads: CrmLead[]; total: number; page: number; pages: number; counts: Record<string, number>; typeCounts: Record<string, number>; waitingCount: number; maxOwners: number }>('crmAllLeads', { ...a, ...f }),
  // to: a salesPersonId, 'turn', or 'junk' (Junk Leads, with a reason)
  moveLeads: (a: CrmAuth, leadIds: string[], to: string, reason = '') => post<{ ok: true; moved: number; skipped: number; waiting: number }>('crmMoveLeads', { ...a, leadIds, to, reason }),
  updateLead: (a: CrmAuth, leadId: string, changes: LeadRow) => post<{ ok: true; lead: CrmLead }>('crmUpdateLead', { ...a, leadId, ...changes }),
  teamToday: (a: CrmAuth) => post<{ ok: true; today: string; working: boolean; team: TeamRow[]; waiting: number; holiday?: HolidayNote }>('crmTeamToday', a),
  // ── The Monitor ──
  monitor: (a: CrmAuth, f: MonitorRange & { salesPersonId?: string }) => post<MonitorData & { ok: true }>('crmMonitor', { ...a, ...f }),
  // The leads behind a number: kind is one of the counts (or given)
  monitorLeads: (a: CrmAuth, f: MonitorRange & { kind: CountKey; heard?: string; salesPersonId?: string }) =>
    post<{ ok: true; kind: CountKey; total: number; rows: MonitorLeadRow[]; maxOwners: number }>('crmMonitorLeads', { ...a, ...f }),
  leadStatus: (a: CrmAuth) => post<LeadStatusData & { ok: true }>('crmLeadStatus', a),
  // kind: a stage, or open / toCall / pollsDue / pollsLate / paymentsDue / late / waiting
  statusLeads: (a: CrmAuth, kind: string, salesPersonId = '') =>
    post<{ ok: true; kind: string; leads: CrmLead[]; maxOwners: number }>('crmStatusLeads', { ...a, kind, salesPersonId }),
  eodDay: (a: CrmAuth, date = '') => post<EodDayData & { ok: true }>('crmEodDay', { ...a, date }),
  // ── Holidays ──
  holidays: (a: CrmAuth) => post<HolidaysData & { ok: true }>('crmHolidays', a),
  addHoliday: (a: CrmAuth, f: { from: string; to?: string; name: string }) =>
    post<HolidaysData & { ok: true; added: string[]; skipped: { date: string; label: string; why: string }[]; moved: number }>('crmAddHoliday', { ...a, ...f }),
  removeHoliday: (a: CrmAuth, date: string) => post<HolidaysData & { ok: true; removed: string }>('crmRemoveHoliday', { ...a, date }),
  setPaused: (a: CrmAuth, salesPersonId: string, paused: boolean) => post<{ ok: true; paused: boolean }>('crmSetPaused', { ...a, salesPersonId, paused }),
  // A Lead Manager / Sales Admin taking calls or not (salesPersonId: '' = themselves)
  setTakesLeads: (a: CrmAuth, takesLeads: boolean, salesPersonId = '') => post<{ ok: true; salesPersonId: string; takesLeads: boolean }>('crmSetTakesLeads', { ...a, takesLeads, salesPersonId }),
  getSettings: (a: CrmAuth) => post<{ ok: true; rules: CrmRules; lists: CrmLists; people: CrmPerson[]; updatedBy: string; updatedAt: string | null }>('crmGetSettings', a),
  saveSettings: (a: CrmAuth, rules: Partial<CrmRules>, lists?: Partial<CrmLists>) =>
    post<{ ok: true; rules: CrmRules; lists: CrmLists; people: CrmPerson[]; updatedBy: string; updatedAt: string | null }>('crmSaveSettings', { ...a, rules, lists }),
  moveHistory: (a: CrmAuth, f: { date?: string; salesPersonId?: string; page?: number }) =>
    post<{ ok: true; moves: CrmActivity[]; total: number; page: number; pages: number }>('crmMoveHistory', { ...a, ...f }),
};

// ── Student Coordinator meetings: the coordinator's side ──
// A Student Coordinator signs in on the mentor login and gets a coordToken
export type CoordAuth = { coordToken: string };
// The poll a coordinator fixes with the student: its status is due pollHours
// after it goes out; pollMorning is the next working day's start (a quick
// button); pollUntil is only for builds before 31 Oct 2026
export interface PollRules { pollHours: number; pollMorning?: string; pollUntil?: string }
// toSend: polls whose time (fixed with the student) is still to come;
// waitingForm: marked Converted, their registration form not in yet
// open: meetings nobody took yet that they can take (the first link takes
// it); needLink lists those too (for older screens), with open: true
export interface MineData extends PollRules {
  now: string; name: string; open?: Meeting[]; needLink: Meeting[]; upcoming: Meeting[]; toMark: Meeting[]; past: Meeting[];
  toSend?: CrmLead[]; polls: CrmLead[]; interested: CrmLead[]; noResponse: CrmLead[]; waitingForm?: CrmLead[]; hasTimes: boolean;
  counts: {
    open?: number; needLink: number; upcoming: number; toMark: number; toSend?: number; polls: number; pollsDue: number; interested: number; paymentsDue: number; noResponse: number;
    waitingForm?: number; formIn?: number;
  };
}
export interface CoordLeadData extends PollRules {
  full: boolean; handles: boolean; lead: Partial<CrmLead> & { id: string; name: string; stage: Stage };
  meetings: Meeting[]; history: CrmActivity[]; canUpdate: boolean;
}
export interface MeetTimesData {
  minutes: number; daysAhead: number; closeBefore: number; weekdays: { day: number; name: string }[];
  times: { day: number; from: string; to: string }[]; off: { date: string; label: string }[]; slotsPerWeek: number;
  week: { date: string; label: string; free: { slot: string; label: string }[]; booked: { id: string; slot: string; label: string; name: string; status: MeetingStatus }[] }[];
  outside?: { id: string; when: string; name: string }[];
}
export type CoordStatus = 'Poll Sent' | PollAnswer;

export const meet = {
  mine: (a: CoordAuth) => post<MineData & { ok: true }>('meetMine', a),
  // On an open meeting the first link takes it (taken: true)
  setLink: (a: CoordAuth, meetingId: string, link: string) => post<{ ok: true; meeting: Meeting; taken?: boolean }>('meetSetLink', { ...a, meetingId, link }),
  // After the meeting: joined (the brochure went, and the poll at the time
  // fixed with the student: now or later), not interested, or didn't join
  outcome: (a: CoordAuth, f: { meetingId: string; joined: boolean; next?: 'poll' | 'not-interested'; pollSentAt?: string; reason?: string; note?: string }) =>
    post<{ ok: true; meeting: Meeting; lead: CrmLead }>('meetOutcome', { ...a, ...f }),
  pollStatus: (a: CoordAuth, f: { leadId: string; status: CoordStatus; pollSentAt?: string; nextAt?: string; reason?: string; note?: string }) =>
    post<{ ok: true; lead: CrmLead }>('meetPollStatus', { ...a, ...f }),
  // One tap: the payment record comes from the student's registration form
  converted: (a: CoordAuth, f: { leadId: string; note?: string }) => post<{ ok: true; lead: CrmLead }>('meetConverted', { ...a, ...f }),
  cancel: (a: CoordAuth, meetingId: string, reason: string) => post<{ ok: true; meeting: Meeting }>('meetCancel', { ...a, meetingId, reason }),
  lead: (a: CoordAuth, leadId: string) => post<CoordLeadData & { ok: true }>('meetLead', { ...a, leadId }),
  fileLink: (a: CoordAuth, activityId: string, file: 'recording' | 'screenshot') => post<{ ok: true; url: string; contentType: string; fileName: string }>('meetFileLink', { ...a, activityId, file }),
  times: (a: CoordAuth) => post<MeetTimesData & { ok: true }>('meetTimes', a),
  setTimes: (a: CoordAuth, times: MeetTimesData['times'], off: string[]) => post<MeetTimesData & { ok: true }>('meetSetTimes', { ...a, times, off }),
  dashboard: (a: CoordAuth, f: MonitorRange) => post<MeetDashboardData & { ok: true }>('meetDashboard', { ...a, ...f }),
  incentive: (a: CoordAuth | { adminPassword: string }, coordinatorId = '') => post<MeetIncentiveData & { ok: true }>('meetIncentive', { ...a, coordinatorId }),
  markPaid: (adminPassword: string, paymentId: string, undo = false) => post<{ ok: true; row: IncentiveRow }>('markCoordIncentivePaid', { adminPassword, paymentId, undo }),
};

// A recording or payment screenshot goes up first; Log call then names it
// (a coordinator: only a payment screenshot, for a lead they handle)
// The P2P board's actions: each one answers with the case as it is now
export type P2PAction = 'p2pTake' | 'p2pTakeOver' | 'p2pCsTake' | 'p2pSetLink' | 'p2pMove' | 'p2pCancel' | 'p2pJoined' | 'p2pResend' | 'p2pBalance'
  | 'p2pRestDate' | 'p2pNotPaying' | 'p2pSetTest' | 'p2pResult' | 'p2pOfferAts' | 'p2pRefund' | 'p2pSendAts';
export type P2PResult = { ok: true; case: P2PCase };

// A student's resume (it goes on the lead at once; the answer has the lead)
// or the ATS resume made for a P2P student: PDF or Word, up to 5 MB
export async function uploadCrmDoc(a: CrmAuth, leadId: string, kind: 'resume' | 'ats', file: File): Promise<{ file: CrmFile; lead?: CrmLead }> {
  const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream', 'X-Secret': SHARED_SECRET };
  if ('crmToken' in a) headers['X-Crm-Token'] = a.crmToken;
  else headers['X-Admin-Password'] = a.adminPassword;
  let resp: Response;
  try {
    resp = await fetch(`${WEB_APP_URL}/crm-upload?leadId=${encodeURIComponent(leadId)}&kind=${kind}&name=${encodeURIComponent(file.name)}`, { method: 'POST', headers, body: file });
  } catch {
    throw new Error('Upload failed — check your connection');
  }
  const data = await resp.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || 'Upload failed');
  return { file: data.file as CrmFile, lead: data.lead as CrmLead | undefined };
}

export async function uploadCrmFile(a: CrmAuth | CoordAuth, leadId: string, kind: 'recording' | 'screenshot', file: File): Promise<CrmFile> {
  const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream', 'X-Secret': SHARED_SECRET };
  if ('coordToken' in a) headers['X-Coord-Token'] = a.coordToken;
  else if ('crmToken' in a) headers['X-Crm-Token'] = a.crmToken;
  else headers['X-Admin-Password'] = a.adminPassword;
  let resp: Response;
  try {
    resp = await fetch(`${WEB_APP_URL}/crm-upload?leadId=${encodeURIComponent(leadId)}&kind=${kind}&name=${encodeURIComponent(file.name)}`, { method: 'POST', headers, body: file });
  } catch {
    throw new Error('Upload failed — check your connection');
  }
  const data = await resp.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || 'Upload failed');
  return data.file as CrmFile;
}

// crmFileLink returns a path under the API ('/crm-file?...')
export const crmFileUrl = (path: string) => `${WEB_APP_URL}${path}`;
